/**
 * A supplier's reply mail, in a docked panel beside the table.
 *
 * ---- Why a dock and not an inline block ----
 *
 * This screen has to work with a dozen suppliers quoting fifty lines each. Everything rendered
 * inline — the mail preview, the priced lines, the parse button — turns the one table a purchaser
 * scans into a page they have to scroll past. Clicking a SUPPLIER opens their correspondence here;
 * clicking the RFQ expands its lines in the table. Two questions, two places, and the table itself
 * never grows because of the mail.
 *
 * Same `.srcdock` surface the RFQ builder's `SourcePanel` uses, deliberately: it is the established
 * "read the mail beside the work" pattern in this app, and a second dock with its own geometry
 * would be a new thing to learn for no gain.
 *
 * ---- The body is rendered as TEXT ----
 *
 * `htmlToText`, never `dangerouslySetInnerHTML`. A supplier's mail is attacker-controlled input
 * arriving from outside the company, and this panel's whole job is opening it — the same reasoning
 * `SourcePanel` and `ReaderPane` record for the same decision.
 */

import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { FileLink } from '../../components/FileLink'
import { Button, Placeholder } from '../../components/ui'
import { AttachmentCard, AttachmentPreviewBody } from '../mail/AttachmentPreview'
import { useMailMessage } from '../../lib/api/useMail'
import { htmlToText } from '../../lib/mail'
import { daysAwaiting, describeAwaiting } from '../../lib/rfq'
import { useParsedOfferState } from './ReplyOffer'
import { buildQuotationFromWorkbook, toTransactionDate } from '../../lib/supplierQuotationFromWorkbook'
import type { RfqReply } from '../../lib/api/useRfqReplies'
import type { MailAttachment, MailMessage, Rfq, EnquiryItem } from '../../lib/types'

/**
 * One reply. Its own component because each needs its own `useMailMessage` — a hook cannot be
 * called inside a `.map()`, and an enquiry can have several replies against one RFQ.
 */
function ReplyBlock({
  reply,
  rfq,
  enquiryId,
  company,
  enquiryLines,
  parsing,
  onFile,
  onParse,
  onPreview,
}: {
  reply: RfqReply
  rfq: Rfq
  enquiryId: string
  company: string
  enquiryLines: readonly EnquiryItem[]
  parsing: boolean
  /** File the quotation from the workbook WE read. */
  onFile: (payload: ReturnType<typeof buildQuotationFromWorkbook>) => void
  /** Ask the server to read the mail — only when there is no workbook we can read. */
  onParse: () => void
  onPreview: (attachment: MailAttachment, mail: MailMessage) => void
}) {
  const rfqId = rfq.id
  const message = useMailMessage(reply.id)
  const mail = message.data ?? null
  const age = describeAwaiting(daysAwaiting(reply.received_at, new Date()))

  /*
    The workbook we have ALREADY read, if this reply carries one. When it does, the quotation is
    built here and `create_supplier_quotation` merely records it — the server never re-reads the
    file. `parse_supplier_reply` has failed twice in two days on the same column (B4, then B5), and
    both times the prices were sitting on screen, readable, while the flow was blocked.
  */
  const { offer, reading, downloadFailed } = useParsedOfferState(reply.id, rfqId)

  return (
    <div style={{ borderTop: '1px solid var(--line)', padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{reply.sender || 'unknown sender'}</div>
          <div style={{ color: 'var(--muted)', fontSize: 12 }}>
            replied {age}
            {reply.subject ? ` · ${reply.subject}` : ''}
          </div>
        </div>
        {!reply.seen ? <Badge tone="info">unread</Badge> : null}
        {/* The server links most replies; this one we placed ourselves because it names the RFQ.
            Said out loud, because a matched reply is a very strong inference and a linked one is
            certainty — and only the first can ever be attached to the wrong RFQ. */}
        {reply.linked === false ? (
          <Badge tone="muted" title={`Not linked by the server — matched because the mail names ${rfqId}.`}>
            matched by RFQ number
          </Badge>
        ) : null}
        <Button
          size="sm"
          variant="primary"
          // Never the server parser while the workbook is still being read (2026-09-19) — see
          // `useParsedOfferState`: that is how a priced reply was filed at 0.00.
          disabled={parsing || reading || downloadFailed}
          title={
            reading
              ? 'Reading the returned workbook…'
              : downloadFailed
                ? 'The returned workbook could not be downloaded — reload the page and try again. The server parser is not offered here because it would file the reply at 0.00.'
                : undefined
          }
          onClick={() =>
            offer === null
              ? // No readable workbook — a prose reply, or their own format. The server's parser is
                // the only thing that can attempt those, so it stays as the fallback.
                onParse()
              : onFile(
                  buildQuotationFromWorkbook({
                    offer,
                    rfq,
                    enquiryId,
                    company,
                    // The supplier's quotation is dated when THEY sent it. Stamping today would
                    // misdate their offer, and validity windows are read off this.
                    transactionDate: toTransactionDate(reply.received_at) ?? new Date().toISOString().slice(0, 10),
                    fallbackCurrency: '',
                    enquiryLines,
                  }),
                )
          }
        >
          {parsing ? 'Filing…' : reading ? 'Reading the workbook…' : downloadFailed ? 'Workbook not downloaded — reload' : offer === null ? 'Read as quotation' : 'File as quotation'}
        </Button>
      </div>

      {message.isPending ? (
        <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 8 }}>Loading the message…</div>
      ) : message.isError ? (
        <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 8 }}>
          {(message.error as Error).message}
        </div>
      ) : mail ? (
        <>
          {mail.attachments.length > 0 ? (
            <>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, margin: '10px 0 4px' }}>
                {mail.attachments.length} attachment{mail.attachments.length === 1 ? '' : 's'}
              </div>
              <div className="attgrid">
                {mail.attachments.map((attachment) => (
                  <AttachmentCard
                    key={attachment.id}
                    attachment={attachment}
                    onPreview={() => onPreview(attachment, mail)}
                  />
                ))}
              </div>
            </>
          ) : null}

          <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 700, margin: '12px 0 4px' }}>Message</div>
          {/*
            Measured caveat worth keeping in front of whoever reads this: on a real reply the body
            is our OWN outgoing mail quoted back, because the supplier wrote nothing and put their
            prices in the attached workbook. So an apparently empty-looking message is not evidence
            they failed to quote — the workbook above is where to look, and the table's expanded
            lines already read it.
          */}
          <div className="rtext">{htmlToText(mail.body_html) || '(empty body)'}</div>
        </>
      ) : null}
    </div>
  )
}

export function SupplierReplyPanel({
  rfq,
  enquiryId,
  company,
  supplierName,
  enquiryLines,
  replies,
  parsingReplyId,
  onFile,
  onParse,
  onClose,
}: {
  rfq: Rfq
  enquiryId: string
  company: string
  supplierName: string
  /** For each filed row's description — see `BuildQuotationInput.enquiryLines`. */
  enquiryLines: readonly EnquiryItem[]
  replies: RfqReply[]
  parsingReplyId: string | null
  onFile: (communication: string, result: ReturnType<typeof buildQuotationFromWorkbook>) => void
  onParse: (rfqId: string, communication: string) => void
  onClose: () => void
}) {
  const rfqId = rfq.id
  const [preview, setPreview] = useState<{ attachment: MailAttachment; mail: MailMessage } | null>(null)

  return (
    <>
      {/*
        An attachment opens IN the dock, widened — the same as the RFQ builder's source panel
        (Akhil, 2026-09-19) — with the quotations table still beside it. It used to open Mail's
        centred modal over everything.
      */}
      <div className={`srcdock${preview ? ' srcdock-wide' : ''}`} role="complementary" aria-label={`Replies from ${supplierName}`}>
        <div className="srcdock-head">
          {preview ? (
            <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="button" className="linkish" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => setPreview(null)}>
                ← Replies
              </button>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {preview.attachment.name}</div>
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                  {preview.attachment.size}
                  {preview.attachment.preview_url ? (
                    <>
                      {' · '}
                      <FileLink href={preview.attachment.preview_url} name={preview.attachment.name}>
                        Download
                      </FileLink>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {supplierName}
              </div>
              <div className="mono" style={{ color: 'var(--muted)', fontSize: 12 }}>
                {rfqId}
              </div>
            </div>
          )}
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </div>

        <div className="srcdock-body">
          {preview ? (
            <AttachmentPreviewBody attachment={preview.attachment} />
          ) : replies.length === 0 ? (
            <Placeholder>
              Nothing back from {supplierName} yet on {rfqId}.
            </Placeholder>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                {replies.length} {replies.length === 1 ? 'reply' : 'replies'} against this RFQ. Filing one records it
                as a Supplier Quotation and puts its prices in the comparison.
              </div>
              {replies.map((reply) => (
                <ReplyBlock
                  key={reply.id}
                  reply={reply}
                  rfq={rfq}
                  enquiryId={enquiryId}
                  company={company}
                  enquiryLines={enquiryLines}
                  parsing={parsingReplyId === reply.id}
                  onFile={(result) => onFile(reply.id, result)}
                  onParse={() => onParse(rfqId, reply.id)}
                  onPreview={(attachment, mail) => setPreview({ attachment, mail })}
                />
              ))}
            </>
          )}
        </div>
      </div>

    </>
  )
}
